import {
    AdminOrganizationDTO,
    JourneyPhase,
    LocalContent,
    PrepCheck,
    PrepCheckAnswer,
    PrepCheckAnswerKey,
    PrepCheckAnswerOption,
    PrepCheckCheckboxAnswerItem,
    PrepCheckDND1DAnswerItem,
    PrepCheckDND2DAnswerItem,
    PrepCheckQuestion,
    ProductRetailer,
    UnformattedPromotedProduct,
    UserFacingHazardStage,
    SponsorMessageWithWebsiteVisibility,
    UnformattedCommunitySponsor,
    CrisisSupportOption,
    CSOScope,
    SupportedContentBooster,
    Hazard,
    LanguageCode,
    SupportedHazardType,
    ContentBlock,
    ImageWithTranslatedAlt,
} from '../entities'
import titleCase from 'voca/title_case'

const addTranslations = (
    target: { [key: string]: any },
    source: { [key: string]: any },
    key: string,
    translated_key: string
) => {
    delete source[translated_key]['spanish']
    delete source[translated_key]['id']
    delete source[translated_key]['tl_tagalog']

    for (const lang of Object.keys(source[translated_key])) {
        const lang_code = lang.split('_')[0]
        if (Object.keys(source[translated_key]).includes(lang)) {
            target[key][lang_code] = source[translated_key][lang]
                ? source[translated_key][lang].trim().replace(/  +/g, ' ')
                : null
        }
    }

    return target[key]
}

const sortContentBlocks = (a: ContentBlock, b: ContentBlock) => {
    if (a.critical) return -1
    else if (a.priority === b.priority) {
        if (a.toggles.includes('Adults')) return -1
        else return a.toggles.includes('Pets') ? -1 : 1
    } else {
        return a.priority - b.priority
    }
}

export const formatHazard = async (h: { [key: string]: any }) => {
    const hazard: Partial<
        Hazard & { emergency_items_block_icon?: ImageWithTranslatedAlt }
    > = {}

    // id
    hazard.id = h.id

    // translations
    hazard.translations = [LanguageCode.ENGLISH]
    if (h.translations) {
        Object.entries(h.translations).forEach(([key, val]) => {
            if (key !== 'id' && key.split('_')[0] !== 'tl' && val === true) {
                hazard.translations?.push(key.split('_')[0] as LanguageCode)
            }
        })
    }

    // tags
    hazard.tags = h.tags
        .trim()
        .split(', ')
        .map((t: string) => t.trim().replace(/  +/g, ' ').toLowerCase())
        .filter((t: string) => t !== '')
    if (h.translated_tags) {
        hazard.tags = addTranslations(hazard, h, 'tags', 'translated_tags')
        Object.entries(h.translated_tags).forEach(([k, v]) => {
            if (v) hazard.tags?.push(...v.toString().split(', '))
        })
    }

    // default stage
    hazard.default_stage = h.default_stage

    // name
    hazard.name = {
        en: h.name.trim().replace(/  +/g, ' '),
    }
    if (h.translated_name) {
        hazard.name = addTranslations(hazard, h, 'name', 'translated_name')
    }

    // slug
    hazard.slug =
        hazard.name?.[LanguageCode.ENGLISH]
            .toLowerCase()
            .replace(/ /g, '-')
            .replace(/,/g, '-') ?? ''

    // description
    hazard.description = {
        en: h.description.trim().replace(/  +/g, ' '),
    }
    if (h.translated_description) {
        hazard.description = addTranslations(
            hazard,
            h,
            'description',
            'translated_description'
        )
    }

    // types
    if (!h.types || h.types.length === 0) hazard.types = []
    else
        hazard.types = h.types.map(
            (t: { type: string }) => t.type as SupportedHazardType
        )

    // filters
    const removed_filters = ['Man_Made']
    if (!h.filters || h.filters.length === 0) hazard.filters = []
    else
        hazard.filters = h.filters
            .map((f: any[]) => f.filter)
            .filter((f: string) => !removed_filters.includes(f))

    // icon
    let response: Response
    hazard.icon = {
        alt: {},
        src: '',
        url: h.icon.url,
    }
    if (process.env.NODE_ENV === 'production') {
        response = await fetch(h.icon.url)
        hazard.icon.src = await response.text()
    }
    try {
        hazard.icon.alt[LanguageCode.ENGLISH] = h.icon.alternativeText
            .trim()
            .replace(/  +/g, ' ')
    } catch (err) {
        throw new Error(
            `Missing or invalid alt text for hazard icon for ${hazard.name?.[LanguageCode.ENGLISH] ?? ''}`
        )
    }
    if (
        h.translated_icon_alt_text &&
        hazard.icon.alt[LanguageCode.ENGLISH] !== ''
    ) {
        hazard.icon.alt = addTranslations(
            hazard.icon,
            h,
            'alt',
            'translated_icon_alt_text'
        )
    } else {
        hazard.translations
            .filter((lang) => lang !== 'en')
            .forEach((lang) => {
                if (!hazard.icon) return
                hazard.icon.alt[lang] = ''
            })
    }

    // cover image
    hazard.cover_image = {
        alt: {},
        src: '',
        url: h.cover_image.url,
    }
    if (process.env.NODE_ENV === 'production') {
        response = await fetch(h.icon.url)
        hazard.cover_image.src = await response.text()
    }
    try {
        hazard.cover_image.alt[LanguageCode.ENGLISH] =
            h.cover_image.alternativeText.trim().replace(/  +/g, ' ')
    } catch (err) {
        throw new Error(
            `Missing or invalid alt text for hazard cover image for ${hazard.name?.[LanguageCode.ENGLISH] ?? ''}`
        )
    }
    if (
        h.translated_cover_image_alt_text &&
        hazard.cover_image.alt[LanguageCode.ENGLISH] !== ''
    ) {
        hazard.cover_image.alt = addTranslations(
            hazard.icon,
            h,
            'alt',
            'translated_cover_image_alt_text'
        )
    } else {
        hazard.translations
            .filter((lang) => lang !== 'en')
            .forEach((lang) => {
                if (!hazard.cover_image) return
                hazard.cover_image.alt[lang] = ''
            })
    }

    // emergency items block icon
    if (h.emergency_items_block_icon) {
        hazard.emergency_items_block_icon = {
            alt: {},
            src: '',
            url: h.emergency_items_block_icon.url,
        }
        if (process.env.NODE_ENV === 'production') {
            response = await fetch(h.emergency_items_block_icon.url)
            hazard.emergency_items_block_icon.src = await response.text()
        }
        hazard.emergency_items_block_icon.alt[LanguageCode.ENGLISH] =
            h.emergency_items_block_icon.alternativeText
                .trim()
                .replace(/  +/g, ' ')
        if (
            h.translated_emergency_items_block_icon_alt_text &&
            hazard.emergency_items_block_icon.alt[LanguageCode.ENGLISH] !== ''
        ) {
            hazard.emergency_items_block_icon.alt = addTranslations(
                hazard.emergency_items_block_icon,
                h,
                'alt',
                'translated_emergency_items_block_icon_alt_text'
            )
        } else {
            hazard.translations
                .filter((lang) => lang !== 'en')
                .forEach((lang) => {
                    if (!hazard.emergency_items_block_icon) return
                    hazard.emergency_items_block_icon.alt[lang] = ''
                })
        }
    }

    // supported boosters
    const supported_toggles: Set<SupportedContentBooster> = new Set()

    // content blocks
    hazard.content_blocks = []
    for (const r_cb of h.content_blocks) {
        const cb: Partial<ContentBlock> = {}
        cb.id = r_cb.id
        cb.type = 'content'
        cb.stage = r_cb.stage
        cb.priority = r_cb.priority
        cb.critical = !!r_cb.critical

        // title
        cb.title = {
            en: r_cb.title.trim().replace(/  +/g, ' '),
        }
        if (r_cb.translated_title) {
            cb.title = addTranslations(cb, r_cb, 'title', 'translated_title')
        }

        // header
        cb.header = {
            en: r_cb.header ? r_cb.header.trim().replace(/  +/g, ' ') : '',
        }
        if (r_cb.translated_header) {
            cb.header = addTranslations(cb, r_cb, 'header', 'translated_header')
        }

        // body
        cb.body = {
            en: r_cb.body ? r_cb.body.trim().replace(/  +/g, ' ') : '',
        }
        if (r_cb.translated_body) {
            cb.body = addTranslations(cb, r_cb, 'body', 'translated_body')
        }

        // block_icon
        if (r_cb.block_icon) {
            try {
                cb.block_icon = {
                    alt: {
                        en: r_cb.block_icon.alternativeText
                            .trim()
                            .replace(/  +/g, ' '),
                    },
                    src: '',
                    url: r_cb.block_icon.url,
                }
            } catch (err) {
                throw new Error(
                    `Missing or invalid alt text for content block icon for ${cb.title?.[LanguageCode.ENGLISH] ?? ''} inside ${hazard.name?.[LanguageCode.ENGLISH]}`
                )
            }
            if (process.env.NODE_ENV === 'production') {
                response = await fetch(r_cb.block_icon.url)
                cb.block_icon.src = await response.text()
            }
            if (
                r_cb.translated_block_icon_alt_text &&
                cb.block_icon.alt[LanguageCode.ENGLISH] !== ''
            ) {
                cb.block_icon.alt = addTranslations(
                    cb.block_icon,
                    r_cb,
                    'alt',
                    'translated_block_icon_alt_text'
                )
            } else {
                hazard.translations
                    .filter((lang) => lang !== 'en')
                    .forEach((lang) => {
                        if (!cb.block_icon) return
                        cb.block_icon.alt[lang] = ''
                    })
            }
        } else {
            cb.block_icon = null
        }

        // image
        if (r_cb.image) {
            try {
                cb.image = {
                    alt: {
                        en: r_cb.image.alternativeText
                            .trim()
                            .replace(/  +/g, ' '),
                    },
                    src: '',
                    url: r_cb.image.url,
                }
            } catch (err) {
                throw new Error(
                    `Missing or invalid alt text for content block image for ${cb.title?.[LanguageCode.ENGLISH] ?? ''} inside ${hazard.name?.[LanguageCode.ENGLISH]}`
                )
            }
            if (process.env.NODE_ENV === 'production') {
                response = await fetch(r_cb.image.url)
                cb.image.src = await response.text()
            }
            if (
                r_cb.translated_image_alt_text &&
                cb.image.alt[LanguageCode.ENGLISH] !== ''
            ) {
                cb.image.alt = addTranslations(
                    cb.image,
                    r_cb,
                    'alt',
                    'translated_image_alt_text'
                )
            } else {
                hazard.translations
                    .filter((lang) => lang !== 'en')
                    .forEach((lang) => {
                        if (!cb.image) return
                        cb.image.alt[lang] = ''
                    })
            }
        } else {
            cb.image = null
        }

        // toggles
        cb.toggles = Object.entries(r_cb.content_boosters)
            .filter(([key, val]) => key !== 'id' && val === true)
            .map(([key]) => titleCase(key) as SupportedContentBooster)
        cb.toggles.forEach(supported_toggles.add, supported_toggles)

        // important_sources
        cb.important_sources = r_cb.important_sources.map(
            (r_cb_s: { name: string; link: string }) => ({
                name: r_cb_s.name.trim().replace(/  +/g, ' '),
                link: r_cb_s.link.trim().replace(/  +/g, ' '),
            })
        )

        // local configs
        cb.local = !!r_cb.boundary || !!r_cb.creator?.jurisdiction
        cb.creator_id = r_cb.creator?.id
        cb.area_override = r_cb.boundary
        cb.local_content = []

        // local content
        if (r_cb.local_content) {
            for (const r_lc of r_cb.local_content) {
                const lc: LocalContent = {
                    body: {
                        en: r_lc.body.trim().replace(/  +/g, ' '),
                    },
                    creator_id: r_lc.creator?.id ?? -1,
                    area_override: r_lc.boundary,
                }
                if (r_lc.translated_body) {
                    lc.body = addTranslations(
                        lc,
                        r_lc,
                        'body',
                        'translated_body'
                    )
                }
                cb.local_content.push(lc)
            }
        }

        hazard.content_blocks.push(cb as ContentBlock)
    }
    hazard.supported_toggles = Array.from(supported_toggles)

    // sources
    hazard.sources = h.sources.map((s: { name: string; link: string }) => ({
        name: s.name.trim().replace(/  +/g, ' '),
        link: s.link.trim().replace(/  +/g, ' '),
    }))

    // local configs
    hazard.local = !!h.boundary || !!h.creator?.jurisdiction
    hazard.creator_id = h.creator?.id
    hazard.area_override = h.boundary

    // other variables
    hazard.has_sponsors = false // NOTE:  This is a placeholder value; the true value will be filled in on ISA
    hazard.scene_safety = false
    hazard.always_call_for_help = false
    hazard.toggle_availability = {}
    for (const toggle of hazard.supported_toggles) {
        if (toggle === 'Adults') continue
        hazard.toggle_availability[toggle] = {}
        for (const stage of ['Before', 'During', 'After']) {
            let formatted_stage: UserFacingHazardStage
            if (stage === 'Before') formatted_stage = 'Prepare'
            else if (stage === 'During') formatted_stage = 'React'
            else formatted_stage = 'Recover'
            hazard.toggle_availability[toggle][formatted_stage] =
                hazard.content_blocks.filter(
                    (cb) => cb.stage === stage && cb.toggles.includes(toggle)
                ).length > 0
        }
    }

    hazard.content_blocks.sort(sortContentBlocks)

    // NOTE: Source blocks will always have a block ID of 0
    for (const stage of ['Before', 'During', 'After']) {
        if (
            hazard.content_blocks.filter((cb) => cb.stage === stage).length ===
            0
        ) {
            continue
        }

        const source_cb: ContentBlock = {
            id: 0,
            stage,
            priority: 999,
            title: { en: 'Sources' },
            header: {
                en: '',
            },
            body: {},
            toggles: hazard.supported_toggles,
            type: 'source',
            local_content: [],
            important_sources: [],
            image: {
                alt: {
                    en: 'Sources icon',
                },
                src: '<svg></svg>',
                url: '',
            },
            block_icon: {
                alt: {
                    en: 'Sources icon',
                },
                src: '<svg></svg>',
                url: '',
            },
        }

        for (const lang of hazard.translations) {
            if (lang !== LanguageCode.ENGLISH) {
                source_cb.title[lang] = 'Sources' // TEMP
                source_cb.header[lang] = ''
                if (source_cb.image) source_cb.image.alt[lang] = ''
                if (source_cb.block_icon) source_cb.block_icon.alt[lang] = ''
            }
            source_cb.body[lang] = (hazard.sources ?? [])
                .map((s) => `* [${s.name}](${s.link})`)
                .join('\n')
        }
        hazard.content_blocks.push(source_cb)
    }

    // done!
    return hazard as Hazard
}

export const formatCrisisSupportOption = async (c: {
    [key: string]: any
}): Promise<CrisisSupportOption> => {
    const cso: Partial<CrisisSupportOption> = {}
    cso.id = c.id
    cso.title = c.title.trim().replace(/  +/g, ' ')
    cso.priority = c.priority
    cso.subheader = c.subheader?.trim().replace(/  +/g, ' ') ?? null
    cso.phone = c.phone?.trim().replace(/  +/g, ' ') ?? null
    cso.link = c.link?.trim().replace(/  +/g, ' ') ?? null
    cso.hours = c.hours?.trim().replace(/  +/g, ' ') ?? null
    cso.always_available = c.is_always_available
    cso.who_they_help = c.who_they_help.trim().replace(/  +/g, ' ')
    cso.details = c.details?.trim().replace(/  +/g, ' ') ?? null
    cso.examples = c.examples.trim().replace(/  +/g, ' ')

    cso.filters = Object.entries(c.filters)
        .filter(([, v]) => v === true)
        .map(
            (e) =>
                titleCase(e[0].replace('_', ' ')).replace(
                    ' ',
                    '_'
                ) as SupportedContentBooster
        )

    cso.scope = c.is_local ? CSOScope.LOCAL : CSOScope.NATIONAL
    cso.is_text_line = c.is_text_line

    if (c.icon) {
        cso.icon = {
            alt: c.icon?.alternativeText?.trim().replace(/  +/g, ' ') ?? '',
            src: '',
            url: c.icon?.url,
        }
        if (process.env.NODE_ENV === 'production') {
            const response = await fetch(c.icon?.url)
            cso.icon.src = await response.text()
        }
    } else {
        cso.icon = null
    }

    cso.hazards = (c.hazards ?? []).map((h: { id: any }) => h.id)

    return cso as CrisisSupportOption
}

export const formatCommunitySponsor = async (s: { [key: string]: any }) => {
    const sponsor: Partial<
        UnformattedCommunitySponsor & {
            is_live: boolean
            show_in_app: boolean
        }
    > = {}

    sponsor.id = s.id
    sponsor.name = s.name.trim().replace(/  +/g, ' ')

    if (s.logo) {
        try {
            sponsor.logo = {
                alt: s.logo.alternativeText.trim().replace(/  +/g, ' ') ?? '',
                src: '',
                url: s.logo.url,
            }
        } catch (err) {
            throw new Error(
                `Missing or invalid alt text for logo for ${sponsor.name ?? ''}`
            )
        }
        if (process.env.NODE_ENV === 'production') {
            const response = await fetch(s.logo.url)
            sponsor.logo.src =
                s.logo?.ext === 'svg' ? await response.text() : ''
        }
    } else {
        sponsor.logo = null
    }

    sponsor.messages = []
    for (const m of s.messages) {
        const message: SponsorMessageWithWebsiteVisibility = {
            ...m,
            message: m.message?.trim().replace(/  +/g, ' ') ?? null,
            link: m.link?.trim().replace(/  +/g, ' ') ?? null,
            extra_links: m.extra_links.map(
                (m_el: { name: string; link: string }) => ({
                    name: m_el.name.trim().replace(/  +/g, ' '),
                    link: m_el.link.trim().replace(/  +/g, ' '),
                })
            ),
            hazards: (m.hazards ?? []).map((d: { id: any }) => d.id),
            geo: m.geo ?? null,
        }

        sponsor.messages.push(message)
    }

    if (sponsor.messages.filter((m) => m.show_on_website).length > 1) {
        throw new Error(
            'More than 1 message is specified to show on the website. Only 1 message can be shown on the website for a sponsor.'
        )
    }

    sponsor.is_live = s.is_live
    sponsor.show_in_app = s.show_in_app

    return sponsor as UnformattedCommunitySponsor & {
        is_live: boolean
        show_in_app: boolean
    }
}

export const formatPromotedProduct = async (p: {
    [key: string]: any
}): Promise<UnformattedPromotedProduct> => {
    const product: UnformattedPromotedProduct = {
        id: p.id,
        name: p.name.trim().replace(/  +/g, ' '),
        retailer_id: p.retailer?.id ?? -1,
        image: {
            alt: p.image?.alternativeText?.trim().replace(/  +/g, '') ?? '',
            src: '',
            url: p.image?.url,
        },
        link: p.link.trim().replace(/  +/g, ' '),
        affiliate_link: !!p.affiliate_link,
        hazards: p.hazards?.map((h: { id: any }) => h.id) ?? [],
        stages: p.stages.map((s: { stage: any }) => s.stage),
        geo: p.geo,
        date_created: p.createdAt,
        date_modified: p.updatedAt,
    }
    if (process.env.NODE_ENV === 'production' && p.image) {
        const response = await fetch(p.image.url)
        product.image.src = p.image.ext === 'svg' ? await response.text() : ''
    }

    // TODO: team_member_favorite_message

    return product
}

export const formatProductRetailer = async (p: {
    [key: string]: any
}): Promise<ProductRetailer> => {
    const retailer: ProductRetailer = {
        id: p.id,
        name: p.name.trim().replace(/  +/g, ' '),
    }

    return retailer
}

export const formatPrepCheck = async (pc: {
    [key: string]: any
}): Promise<PrepCheck & { answer_key: PrepCheckAnswerKey[] }> => {
    const prep_check: Partial<
        PrepCheck & { answer_key: PrepCheckAnswerKey[] }
    > = {}

    // id's
    prep_check.id = pc.id
    prep_check.hazard_id = pc.hazard?.id

    // title
    prep_check.title = {
        en: pc.title.trim().replace(/  +/g, ' '),
    }
    if (pc.translated_title) {
        prep_check.title = addTranslations(
            prep_check,
            pc,
            'title',
            'translated_title'
        )
    }

    // dark_icon
    if (pc.dark_icon) {
        try {
            prep_check.dark_icon = {
                alt: {
                    en:
                        pc.dark_icon?.alternativeText
                            .trim()
                            .replace(/  +/g, ' ') ?? '',
                },
                src: '',
                url: pc.dark_icon?.url,
            }
        } catch (err) {
            throw new Error(
                `Missing or invalid alt text for dark icon for ${prep_check.title?.[LanguageCode.ENGLISH] ?? ''}`
            )
        }
        if (
            pc.translated_icon_alt_text &&
            prep_check.dark_icon.alt[LanguageCode.ENGLISH] !== ''
        ) {
            prep_check.dark_icon.alt = addTranslations(
                prep_check.dark_icon,
                pc,
                'alt',
                'translated_icon_alt_text'
            )
        } else {
            Object.keys(prep_check.title ?? {})
                .filter((lang) => lang !== 'en')
                .forEach((lang) => {
                    if (!prep_check.dark_icon) return
                    prep_check.dark_icon.alt[lang] = ''
                })
        }
    }

    // light_icon
    if (pc.light_icon) {
        try {
            prep_check.light_icon = {
                alt: {
                    en:
                        pc.light_icon?.alternativeText
                            .trim()
                            .replace(/  +/g, ' ') ?? '',
                },
                src: '',
                url: pc.light_icon?.url,
            }
        } catch (err) {
            throw new Error(
                `Missing or invalid alt text for light icon for ${prep_check.title?.[LanguageCode.ENGLISH] ?? ''}`
            )
        }
        if (
            pc.translated_icon_alt_text &&
            prep_check.light_icon.alt[LanguageCode.ENGLISH] !== ''
        ) {
            prep_check.light_icon.alt = addTranslations(
                prep_check.light_icon,
                pc,
                'alt',
                'translated_icon_alt_text'
            )
        } else {
            Object.keys(prep_check.title ?? {})
                .filter((lang) => lang !== 'en')
                .forEach((lang) => {
                    if (!prep_check.light_icon) return
                    prep_check.light_icon.alt[lang] = ''
                })
        }
    }

    // questions
    prep_check.questions = []
    for (const q of pc.questions) {
        const question: Partial<PrepCheckQuestion> = {}

        question.id = q.id
        question.question_type = q.question_type
        question.link_to_emergency_items = q.link_to_emergency_items ?? false

        // name
        question.name = {
            en: q.name.trim().replace(/  +/g, ' '),
        }
        if (q.translated_name) {
            question.name = addTranslations(
                question,
                q,
                'name',
                'translated_name'
            )
        }

        // question
        question.question = {
            en: q.question.trim().replace(/  +/g, ' '),
        }
        if (q.translated_question) {
            question.question = addTranslations(
                question,
                q,
                'question',
                'translated_question'
            )
        }

        // subheading
        question.subheading = {
            en: q.subheading?.trim().replace(/  +/g, ' ') ?? '',
        }
        if (q.translated_subheading) {
            question.subheading = addTranslations(
                question,
                q,
                'subheading',
                'translated_subheading'
            )
        }

        // explainer_text
        if (q.explainer_text) {
            question.explainer_text = {
                en: q.explainer_text.trim().replace(/  +/g, ' '),
            }
            if (q.translated_explainer_text) {
                question.explainer_text = addTranslations(
                    question,
                    q,
                    'explainer_text',
                    'translated_explainer_text'
                )
            }
        }

        // image
        if (q.image) {
            try {
                question.image = {
                    alt: {
                        en: q.image.alternativeText.trim().replace(/  +/g, ''),
                    },
                    src: '',
                    url: q.image.url,
                }
            } catch (err) {
                throw new Error(
                    `Missing or invalid alt text for question image for ${question.question?.[LanguageCode.ENGLISH] ?? ''}`
                )
            }
            if (
                q.translated_image_alt_text &&
                question.image.alt[LanguageCode.ENGLISH] !== ''
            ) {
                question.image.alt = addTranslations(
                    question.image,
                    q,
                    'alt',
                    'translated_image_alt_text'
                )
            } else {
                Object.keys(prep_check.title ?? {})
                    .filter((lang) => lang !== 'en')
                    .forEach((lang) => {
                        if (!question.image) return
                        question.image.alt[lang] = ''
                    })
            }
        }

        // options
        question.options = []
        for (const a of q.answers) {
            const answer: Partial<PrepCheckAnswerOption> = {}

            answer.id = a.id

            // text
            answer.text = {
                en: a.cso
                    ? a.cso.title.trim().replace(/  +/g, ' ')
                    : (a.answer?.trim().replace(/  +/g, ' ') ?? ''),
            }
            if (a.translated_answer) {
                if (a.cso) {
                    Object.keys(prep_check.title ?? {})
                        .filter((lang) => lang !== 'en')
                        .forEach((lang) => {
                            if (!answer.text) return
                            const lang_id = lang.split('_')[0]
                            answer.text[lang_id] =
                                answer.text[LanguageCode.ENGLISH]
                        })
                } else {
                    answer.text = addTranslations(
                        answer,
                        a,
                        'text',
                        'translated_answer'
                    )
                }
            }

            // explainer_text
            if (a.answer_explainer_text) {
                answer.explainer_text = {
                    en: a.answer_explainer_text.trim().replace(/  +/g, ' '),
                }
                if (a.translated_answer_explainer_text) {
                    answer.explainer_text = addTranslations(
                        answer,
                        a,
                        'explainer_text',
                        'translated_answer_explainer_text'
                    )
                }
            }

            answer.weight = a.weight
            if (a.icon) {
                try {
                    answer.image = {
                        url: a.icon.url,
                        src: '',
                        alt: {
                            en: a.icon.alternativeText
                                .trim()
                                .replace(/  +/g, ''),
                        },
                    }
                } catch (err) {
                    throw new Error(
                        `Missing or invalid alt text for answer image: ${answer.text?.[LanguageCode.ENGLISH]}, in ${prep_check.title?.[LanguageCode.ENGLISH] ?? ''}`
                    )
                }
                if (
                    a.translated_icon_alt_text &&
                    answer.image.alt[LanguageCode.ENGLISH] !== ''
                ) {
                    answer.image.alt = addTranslations(
                        answer.image,
                        a,
                        'alt',
                        'translated_icon_alt_text'
                    )
                } else {
                    Object.keys(prep_check.title ?? {})
                        .filter((lang) => lang !== 'en')
                        .forEach((lang) => {
                            if (!answer.image) return
                            answer.image.alt[lang] = ''
                        })
                }
            } else if (a.cso) {
                try {
                    answer.image = {
                        url: a.cso.icon?.url,
                        src: '',
                        alt: {
                            en:
                                a.cso.icon?.alternativeText
                                    .trim()
                                    .replace(/  +/g, '') ?? '',
                        },
                    }
                } catch (err) {
                    throw new Error(
                        `Missing or invalid alt text for answer image: ${answer.text?.[LanguageCode.ENGLISH]}, in ${prep_check.title?.[LanguageCode.ENGLISH] ?? ''}`
                    )
                }
                Object.keys(prep_check.title ?? {})
                    .filter((lang) => lang !== 'en')
                    .forEach((lang) => {
                        if (!answer.image) return
                        answer.image.alt[lang] = ''
                    })
            }

            answer.best_stage = a.best_stage
            answer.best_order = a.best_order

            question.options.push(answer as PrepCheckAnswerOption)
        }

        prep_check.questions.push(question as PrepCheckQuestion)
    }

    prep_check.answer_key = []
    for (const q of prep_check.questions) {
        const questionKey: PrepCheckAnswerKey = {
            question_id: q.id,
            answers: [],
        }
        for (const a of q.options) {
            const answer: Partial<
                PrepCheckAnswer<
                    | PrepCheckDND1DAnswerItem
                    | PrepCheckDND2DAnswerItem
                    | PrepCheckCheckboxAnswerItem
                >
            > = {
                answer_id: a.id,
            }
            answer.answer_id = a.id
            switch (q.question_type) {
                case 'OneD_Ordered_List': {
                    answer.answer = {
                        id: a.id,
                        order: a.best_order ?? -1,
                    }
                    break
                }

                case 'TwoD_Grid_Placement': {
                    answer.answer = {
                        id: a.id,
                        placement: [
                            a.best_stage ?? 'Before',
                            a.best_order ?? -1,
                        ],
                    }
                    break
                }

                case 'Checkboxes': {
                    answer.answer = {
                        id: a.id,
                        checked: true,
                    }
                    break
                }

                default: {
                    break
                }
            }

            questionKey.answers.push(answer as PrepCheckAnswer)
        }

        prep_check.answer_key.push(questionKey)
    }

    // novice_badge
    if (pc.novice_badge) {
        try {
            prep_check.novice_badge = {
                alt: {
                    en: pc.novice_badge.alternativeText
                        .trim()
                        .replace(/  +/g, ''),
                },
                url: pc.novice_badge.url,
                src: '',
            }
        } catch (err) {
            throw new Error(
                `Missing or invalid alt text for novice badge image for ${prep_check.title?.[LanguageCode.ENGLISH] ?? ''}`
            )
        }

        if (
            pc.translated_novice_badge_alt_text &&
            prep_check.novice_badge.alt[LanguageCode.ENGLISH] !== ''
        ) {
            prep_check.novice_badge.alt = addTranslations(
                prep_check.novice_badge,
                pc,
                'alt',
                'translated_novice_badge_alt_text'
            )
        } else {
            Object.keys(prep_check.title ?? {})
                .filter((lang) => lang !== 'en')
                .forEach((lang) => {
                    if (!prep_check.novice_badge) return
                    prep_check.novice_badge.alt[lang] = ''
                })
        }
    }

    // intermediate_badge
    if (pc.intermediate_badge) {
        try {
            prep_check.intermediate_badge = {
                alt: {
                    en: pc.intermediate_badge.alternativeText
                        .trim()
                        .replace(/  +/g, ''),
                },
                url: pc.intermediate_badge.url,
                src: '',
            }
        } catch (err) {
            throw new Error(
                `Missing or invalid alt text for intermediate badge image for ${prep_check.title?.[LanguageCode.ENGLISH] ?? ''}`
            )
        }

        if (
            pc.translated_intermediate_badge_alt_text &&
            prep_check.intermediate_badge.alt[LanguageCode.ENGLISH] !== ''
        ) {
            prep_check.intermediate_badge.alt = addTranslations(
                prep_check.intermediate_badge,
                pc,
                'alt',
                'translated_intermediate_badge_alt_text'
            )
        } else {
            Object.keys(prep_check.title ?? {})
                .filter((lang) => lang !== 'en')
                .forEach((lang) => {
                    if (!prep_check.intermediate_badge) return
                    prep_check.intermediate_badge.alt[lang] = ''
                })
        }
    }

    // advanced_badge
    if (pc.advanced_badge) {
        try {
            prep_check.advanced_badge = {
                alt: {
                    en: pc.advanced_badge.alternativeText
                        .trim()
                        .replace(/  +/g, ''),
                },
                url: pc.advanced_badge.url,
                src: '',
            }
        } catch (err) {
            throw new Error(
                `Missing or invalid alt text for advanced badge image for ${prep_check.title?.[LanguageCode.ENGLISH] ?? ''}`
            )
        }

        if (
            pc.translated_advanced_badge_alt_text &&
            prep_check.advanced_badge.alt[LanguageCode.ENGLISH] !== ''
        ) {
            prep_check.advanced_badge.alt = addTranslations(
                prep_check.advanced_badge,
                pc,
                'alt',
                'translated_advanced_badge_alt_text'
            )
        } else {
            Object.keys(prep_check.title ?? {})
                .filter((lang) => lang !== 'en')
                .forEach((lang) => {
                    if (!prep_check.advanced_badge) return
                    prep_check.advanced_badge.alt[lang] = ''
                })
        }
    }

    // done!
    return prep_check as PrepCheck & { answer_key: PrepCheckAnswerKey[] }
}

export const formatJourneyPhase = async (p: {
    [key: string]: any
}): Promise<JourneyPhase> => {
    const phase: Partial<JourneyPhase> = {}
    phase.id = p.id

    phase.name = p.name.trim().replace(/  +/g, ' ')

    // display_name
    phase.display_name = {
        en: phase.name ?? '',
    }
    if (p.translated_name) {
        phase.display_name = addTranslations(
            phase,
            p,
            'display_name',
            'translated_name'
        )
    }

    // icon
    try {
        phase.icon = {
            alt: {
                en: p.icon.alternativeText.trim().replace(/  +/g, ' '),
            },
            src: '',
            url: p.icon.url,
        }
    } catch (err) {
        throw new Error(
            `Missing or invalid alt text for icon for ${phase.name ?? ''}`
        )
    }
    if (
        p.translated_icon_alt_text &&
        phase.icon.alt[LanguageCode.ENGLISH] !== ''
    ) {
        phase.icon.alt = addTranslations(
            phase.icon,
            p,
            'alt',
            'translated_icon_alt_text'
        )
    } else {
        Object.keys(phase.display_name ?? {})
            .filter((lang) => lang !== 'en')
            .forEach((lang) => {
                if (!phase.icon) return
                phase.icon.alt[lang] = ''
            })
    }

    phase.point_threshold = p.point_threshold

    // subcopy
    phase.subcopy = {
        en: p.subcopy.trim().replace(/  +/g, ' '),
    }
    if (p.translated_subcopy) {
        phase.subcopy = addTranslations(
            phase,
            p,
            'subcopy',
            'translated_subcopy'
        )
    }

    // done!
    return phase as JourneyPhase
}

export const formatOrganization = async (
    o: { [key: string]: any },
    env: 'prod' | 'staging' = 'staging'
): Promise<AdminOrganizationDTO> => {
    const organization: AdminOrganizationDTO = {
        // base config
        id: o.id,
        name: o.name,
        slug: o.slug.trim().replace(/  +/g, ' '),
        jurisdiction: o.jurisdiction,
        jurisdiction_name: o.jurisdiction_name,
        default_zoom_level: o.default_zoom_level,
        base_subscription: o.base_subscription,
        primary_color: o.primary_color,
        trim_color: o.trim_color,
        logo: {
            alt:
                o.logo?.alternativeText?.trim().replace(/  +/g, ' ') ??
                `${o.name} logo`,
            url: o.logo?.url,
            src: '',
        },
        owner_first_name: o.owner_first_name
            ? o.owner_first_name.trim().replace(/  +/g, ' ')
            : undefined,
        owner_last_name: o.owner_last_name
            ? o.owner_last_name.trim().replace(/  +/g, ' ')
            : undefined,
        owner_email: o.owner_email
            ? o.owner_email.trim().replace(/  +/g, ' ')
            : undefined,
        owner_title: o.owner_title
            ? o.owner_title.trim().replace(/  +/g, ' ')
            : undefined,
        stripe_customer_id:
            env === 'prod'
                ? o.stripe_customer_id
                    ? o.stripe_customer_id.trim().replace(/  +/g, ' ')
                    : undefined
                : o.testing_stripe_customer_id
                  ? o.testing_stripe_customer_id.trim().replace(/  +/g, ' ')
                  : undefined,
        visible_hazards: (o.visible_hazards ?? []).map(
            (h: { id: any }) => h.id
        ),
    }

    return organization
}
